<template>
  <v-container class="year-view">
    <v-btn depressed small class="today-btn" @click="resetToToday">
      <v-icon small left>mdi-calendar-today</v-icon>
      Hoje
    </v-btn>

    <v-row class="my-2 d-flex justify-left align-center">
      <v-btn @click="previousYear" icon class="mr-1 nav-btn">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <v-btn @click="nextYear" icon class="mr-4 nav-btn">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>

      <span class="year-label">{{ currentYear }}</span>
    </v-row>

    <v-row>
      <v-col v-for="(month, index) in months" :key="index" cols="3">
        <v-card class="month-calendar" flat>
          <v-date-picker
            v-model="selectedDate"
            :picker-date="month.date"
            :no-title="true"
            locale="pt"
            width="100%"
            show-adjacent-months
            color="primary"
            ref="datePicker"
            @input="onDateSelected"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  props: {
    currentDate: { type: String, default: null },
  },

  data() {
    const todayIsoLocal = this.getTodayLocalISO();
    const year = new Date().getFullYear();

    return {
      currentYear: year,
      selectedDate: this.currentDate || todayIsoLocal,
      months: this.generateMonths(year),
    };
  },

  watch: {
    currentYear(newYear) {
      this.months = this.generateMonths(newYear);
      this.$nextTick(this.disableMonthNavigationHeaders);
    },

    currentDate(newVal) {
      if (newVal) this.selectedDate = newVal;
    },
  },

  mounted() {
    this.$nextTick(this.disableMonthNavigationHeaders);
  },

  methods: {
    nextYear() {
      this.currentYear += 1;
    },

    previousYear() {
      this.currentYear -= 1;
    },

    // ✅ "Hoje" no fuso local do navegador (sem UTC)
    getTodayLocalISO() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    // ✅ Converte Date -> YYYY-MM-DD local (sem UTC)
    toLocalISODate(dateObj) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    generateMonths(year) {
      return Array.from({ length: 12 }, (_, i) => {
        const firstDayLocal = new Date(year, i, 1); // LOCAL
        return { date: this.toLocalISODate(firstDayLocal) };
      });
    },

    resetToToday() {
      const todayIsoLocal = this.getTodayLocalISO();
      this.currentYear = new Date().getFullYear();
      this.selectedDate = todayIsoLocal;

      this.$emit("reset-calendar-to-today");
      this.$emit("calendar-year-date-selected", todayIsoLocal);
    },

    onDateSelected(date) {
      this.selectedDate = date;
      this.$emit("calendar-year-date-selected", date);
    },

    disableMonthNavigationHeaders() {
      const pickers = this.$refs.datePicker;
      if (!pickers) return;

      const list = Array.isArray(pickers) ? pickers : [pickers];

      list.forEach((picker) => {
        const el = picker?.$el;
        if (!el) return;

        const header = el.querySelector(".v-date-picker-header");
        if (!header) return;

        header.style.pointerEvents = "none";

        const prevButton = header.querySelector(
          '[aria-label="Previous month"]',
        );
        const nextButton = header.querySelector('[aria-label="Next month"]');

        if (prevButton) prevButton.style.display = "none";
        if (nextButton) nextButton.style.display = "none";
      });
    },
  },
};
</script>

<style scoped>
.year-view {
  min-height: 100vh;
}

.today-btn {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  color: var(--ds-primary) !important;
  background: var(--ds-blue-100) !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.16) !important;
}

.today-btn:hover {
  background: var(--ds-blue-200) !important;
}

.nav-btn {
  background: var(--ds-blue-50);
  border: 1px solid var(--ds-blue-100);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.nav-btn:hover {
  background: var(--ds-blue-100);
  border-color: var(--ds-blue-200);
}

.nav-btn :deep(.v-icon) {
  color: var(--ds-primary) !important;
}

.year-label {
  font-weight: 800;
  font-size: 22px;
  color: var(--ds-primary);
  letter-spacing: -0.5px;
}

.month-calendar {
  height: 300px;
  border: 1px solid var(--ds-blue-100);
  border-radius: 12px !important;
  overflow: hidden;
  background: var(--ds-surface-white);
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.month-calendar:hover {
  border-color: var(--ds-blue-200);
  box-shadow: 0 6px 18px rgba(31, 77, 63, 0.12);
}
</style>
